<script setup name="UIDAdjustContours">
import { CurveInterpolator } from 'curve-interpolator';
import * as d3 from 'd3';
import { computed, onBeforeUnmount, onMounted, ref, toRefs, watch } from 'vue';

import { nudgePoints2D } from '@/src/core/contours/nudge';
import { useContourContainerStore } from '@/src/store/contours/contour-container-store';
import * as ContourDefinitions from '@/src/store/contours/contour-definitions';

import { encodeContourLabelItemId } from '@/src/store/contours/contour-definitions';
import {
  convertArrayToPointArray,
  convertPointArrayToArray,
} from '@/src/store/contours/contour-processing/convertArrayToPointArray';
import {
  getClosestPointArrayIdx,
  getClosestPointIdx,
} from '@/src/store/contours/contour-processing/getClosestPointIdx';

import {
  ViewportDrawingMode,
  ViewportToolMode,
} from '@/src/store/contours/types/tool-modes-type';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';

import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

import {
  CONTOUR_STROKE,
  ENDPOINT_RADIUS,
} from './contour-draw-constants';
import { getLineGenerator } from './contour-draw-geometry';
import {
  appendArrow,
  appendCircle,
  appendPath,
  applyGroupTransform,
} from './contour-svg-primitives';
import { coordinateTransformation } from './coordinateTransformation';

import useEventBus from './eventBus';
import {
  drawRangeCrossbars,
  getLocalizerDir2D,
  RANGE_CROSS_OPACITY,
  RANGE_CROSS_STROKE,
} from './lax-range-crossbar';
import PieMenu from './PieMenu.vue';
import TextAnnotationInput from './TextAnnotationInput.vue';
import { ThresholdSegmentation } from './ThresholdSegmentation';

defineOptions({
  name: 'UIDAdjustContours',
});

const props = defineProps({
  id: { type: String, required: true },
  active: { type: Boolean, required: true },
  currentImageUid: { type: String, required: true },
  showContours: { type: Boolean, required: true },
  canvasWidth: { type: Number, required: true },
  canvasHeight: { type: Number, required: true },
});

// Add emits:
// TODO: figure out why when annotation is committed, hover state is not re-enabled. As a work around, forcing for now.
const emit = defineEmits(['forceHover']);

// Shared across all UIDAdjustContours instances: ensures only one pie menu is open at a time
const activePieMenuViewId = ref(null);

let rightMouseDown = false;
let rightMouseDownPos = null;
let rightMouseDragged = false;
let pendingContextMenuEvent = null;
let rightDragTimeout = null;
let contextMenuOpenedAt = 0;
let listenerRefCount = 0;

const RIGHT_CLICK_DRAG_THRESHOLD = 5; // px

function isRightMouseButton(e) {
  return e.button === 2 || (e.button === 0 && e.ctrlKey);
}

function onWindowPointerDown(e) {
  if (isRightMouseButton(e)) {
    if (rightDragTimeout) {
      clearTimeout(rightDragTimeout);
      rightDragTimeout = null;
    }
    rightMouseDown = true;
    rightMouseDownPos = { x: e.clientX, y: e.clientY };
    rightMouseDragged = false;
    pendingContextMenuEvent = null;
  }
}

function onWindowPointerMove(e) {
  if (rightMouseDownPos) {
    const dist = Math.hypot(
      e.clientX - rightMouseDownPos.x,
      e.clientY - rightMouseDownPos.y,
    );
    if (dist > RIGHT_CLICK_DRAG_THRESHOLD) {
      rightMouseDragged = true;
      pendingContextMenuEvent = null;
    }
  }
}

function onWindowPointerUp(e) {
  if (!rightMouseDown && !rightMouseDownPos && !pendingContextMenuEvent) {
    return;
  }
  rightMouseDown = false;

  if (rightMouseDownPos) {
    const dist = Math.hypot(
      e.clientX - rightMouseDownPos.x,
      e.clientY - rightMouseDownPos.y,
    );
    if (dist > RIGHT_CLICK_DRAG_THRESHOLD) {
      rightMouseDragged = true;
    }
  }

  // On macOS, `contextmenu` fires on mousedown and is deferred in `pendingContextMenuEvent`.
  // Open the pie menu now on release only if no drag occurred.
  if (pendingContextMenuEvent) {
    const pending = pendingContextMenuEvent;
    pendingContextMenuEvent = null;
    const wasDragged = rightMouseDragged;
    rightMouseDownPos = null;
    rightMouseDragged = false;
    if (!wasDragged && props.showContours) {
      initContextMenu(pending);
    }
    return;
  }

  if (rightMouseDownPos) {
    // Retain rightMouseDragged state for a brief grace period (300ms)
    // so subsequent contextmenu events (which fire on mouseup on macOS)
    // so subsequent contextmenu events (which fire on mouseup on Windows)
    // can reliably detect that a drag gesture took place.
    if (rightDragTimeout) clearTimeout(rightDragTimeout);
    rightDragTimeout = setTimeout(() => {
      rightMouseDownPos = null;
      rightMouseDragged = false;
      rightDragTimeout = null;
    }, 300);
  }
}

function setupRightDragListeners() {
  if (typeof window === 'undefined') return;
  if (listenerRefCount === 0) {
    window.addEventListener('pointerdown', onWindowPointerDown, { capture: true });
    window.addEventListener('pointermove', onWindowPointerMove, { capture: true });
    window.addEventListener('pointerup', onWindowPointerUp, { capture: true });
    window.addEventListener('mouseup', onWindowPointerUp, { capture: true });
  }
  listenerRefCount++;
}

function cleanupRightDragListeners() {
  if (typeof window === 'undefined') return;
  listenerRefCount--;
  if (listenerRefCount <= 0) {
    listenerRefCount = 0;
    window.removeEventListener('pointerdown', onWindowPointerDown, { capture: true });
    window.removeEventListener('pointermove', onWindowPointerMove, { capture: true });
    window.removeEventListener('pointerup', onWindowPointerUp, { capture: true });
    window.removeEventListener('mouseup', onWindowPointerUp, { capture: true });
  }
}
const DISTANCE_CONTOUR_SELECTION_THRESHOLD = 1.25;
const DISTANCE_CENTROID_SELECTION_THRESHOLD = 20;
const DISTANCE_LINE_ENDPOINT_SELECTION_THRESHOLD = 15;
const DISTANCE_POINT_SELECTION_THRESHOLD = 15;
const MIN_SELECTION_CLICK_TRAVEL_DISTANCE = 5;
const THRESHOLD_SEGMENTATION_INNER_REGIONS = true;

const nudgePolygon = ref([]);
const nudgeRadius = ref(0);
// mouse event handling states
const mouseLeftButtonDown = ref(false);
const mouseRightButtonDown = ref(false);
const mouseMiddleButtonDown = ref(false);

const mousePositionButtonDown = ref(null);
const mousePathButtonDown = ref([]);
const mousePathButtonDownThreshold = ref([]);
const contourProbsButtonDown = ref(null);
const lineEndPointProbsButtonDown = ref(null);
const centroidContourProbsButtonDown = ref(null);
const pointContourProbsButtonDown = ref(null);

const movingPointProbs = ref(null);
const movingLineEndpoints = ref(null);

const mouseActionDrawContour = ref(false);
const mouseActionMoveLineEndPoint = ref(false);
const mouseActionMoveCentroid = ref(false);
const mouseActionMovePoint = ref(false);
const mouseActionSkipPointDraw = ref(false);
const mouseActionNudgeTool = ref(false);
const mouseActionThreshold = ref(false);
const thresholdSegmentation = ref(null);

// const viewSliceStore = useViewSliceStore();
const currentStudyStore = useCurrentStudyStore();
const contourContainerStore = useContourContainerStore();
const viewportStateStore = useViewportStateStore();

// handle props
const {
  id: viewID,
  active,
  currentImageUid: curImageID,
  showContours,
  canvasWidth,
  canvasHeight,
} = toRefs(props);

// coordinate transformations composable
const {
  scaleFactor,
  imageToScreen,
  screenToImage,
  getCurrentCoordinates,
  mouseEventToScreen,
  updateLeftCornerCoords,
  imageToScreenMatrix,
  overlayTransform,
} = coordinateTransformation({
  currentImageUid: curImageID,
  id: viewID,
  canvasWidth,
  canvasHeight,
});

// helper functions

// ui
const annotationInputVisible = ref(false);
const annotationInputPosition = ref({ x: 0, y: 0 });
const annotationInputValue = ref(''); // Can be used in the future to enable text editing, but for now user can just delete and add new.
const annotationInputRef = ref(null);
const pendingAnnotationContour = ref(null);

// const currentPointer = ref('default');
const showContextMenu = ref(false);
// const targetContext = ref(null);
const contextMenuCoords = ref([0, 0]);
// const clipboard = ref([]); // Clipboard:

const currentDrawingContours = computed(
  () => contourContainerStore.getDrawingContours(curImageID.value) || null,
);
const selectedContour = computed(() =>
  viewportStateStore.getSelectedContour(viewID.value),
);
const hasSelectedContour = computed(() =>
  Boolean(
    selectedContour.value?.label
    && selectedContour.value?.id != null
    && selectedContour.value?.id !== encodeContourLabelItemId([-1]),
  ),
);

const currentDrawingContourLabel = computed(() =>
  viewportStateStore.getDrawingContour(viewID.value),
);
const currentDrawingContourProps = computed(() => {
  const properties
    = ContourDefinitions.ContourDefinitions[currentDrawingContourLabel.value];
  return properties ? { ...properties } : null;
});

const localizerDir2D = computed(() =>
  getLocalizerDir2D(viewID.value, curImageID.value, viewportStateStore, currentStudyStore),
);

// ContextMenu
const overlayContainer = ref(null);
const pieMenuContainerRef = ref(null);

// contour drawing
const overlay = ref(null);
const svg = ref(null);

function initContextMenu(e) {
  if (!overlayContainer.value) {
    console.warn('Overlay container not yet available');
    return;
  }

  const rect = overlayContainer.value.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;
  contextMenuCoords.value = [x, y];
  activePieMenuViewId.value = viewID.value;
  contextMenuOpenedAt = performance.now();
  showContextMenu.value = true;
}

function closeContextMenu() {
  if (activePieMenuViewId.value === viewID.value) {
    activePieMenuViewId.value = null;
  }
  showContextMenu.value = false;
  contextMenuCoords.value = [0, 0];
}

watch(activePieMenuViewId, (currentActiveViewId) => {
  if (currentActiveViewId !== viewID.value && showContextMenu.value) {
    closeContextMenu();
  }
});

function onGlobalPointerDown(e) {
  if (!showContextMenu.value || !pieMenuContainerRef.value) return;
  const pieMenuEl = pieMenuContainerRef.value.querySelector('.pie-menu');
  if (pieMenuEl && !pieMenuEl.contains(e.target)) {
    closeContextMenu();
  }
}

watch(showContextMenu, (isOpen) => {
  if (isOpen) {
    setTimeout(() => {
      if (showContextMenu.value && typeof window !== 'undefined') {
        window.addEventListener('pointerdown', onGlobalPointerDown, { capture: true });
      }
    }, 0);
  }
  else if (typeof window !== 'undefined') {
    window.removeEventListener('pointerdown', onGlobalPointerDown, { capture: true });
  }
});

// ToDo review
const { onEvent } = useEventBus(viewID);

function showAnnotationInput({ event, contourLine, label, id }) {
  const inputWidth = 160;
  if (!overlayContainer.value) return;

  const rect = overlayContainer.value.getBoundingClientRect();
  let x = event.clientX - rect.left;
  const y = event.clientY - rect.top;

  // x is now the CENTER of the (translate(-50%,-50%)) input
  const half = inputWidth / 2;
  x = Math.min(Math.max(x, half), rect.width - half);

  annotationInputPosition.value = { x, y };
  pendingAnnotationContour.value = { contourLine, label, id };
  annotationInputVisible.value = true;
}

function hideAnnotationInput() {
  annotationInputVisible.value = false;
  pendingAnnotationContour.value = null;
}

function forceAnnotationCommit() {
  annotationInputRef.value?.commitAnnotationInput();
}

function commitAnnotationInput(userInputText) {
  if (!annotationInputVisible.value || !pendingAnnotationContour.value) {
    cancelAnnotationInput();

    return;
  }
  const { contourLine, label, id } = pendingAnnotationContour.value;

  const text = userInputText?.trim();
  if (text && text.length > 0) {
    contourContainerStore.addContour(
      curImageID.value,
      convertArrayToPointArray(contourLine),
      label,
      id,
      true, // triggerEvent
      true,
      false,
      text,
    );
  }

  cancelAnnotationInput();
}

function cancelAnnotationInput() {
  hideAnnotationInput();
  stopDrawing();
  // Wait 100ms before re-enabling hover to avoid immediate hover state
  setTimeout(() => {
    emit('forceHover', true);
  }, 100);

  resetMouseStates();
  resetDrawingCanvas();
  viewportStateStore.setToolMode(viewID.value, ViewportToolMode.Pan);
}

function calculateDistance(point1, point2) {
  return Math.sqrt((point1[0] - point2[0]) ** 2 + (point1[1] - point2[1]) ** 2);
}

function calculateDistancePoints(point1, point2) {
  return Math.sqrt((point1.x - point2.x) ** 2 + (point1.y - point2.y) ** 2);
}

function equalPoints(point1, point2) {
  return point1[0] === point2[0] && point1[1] === point2[1];
}

function pointToPolylineDistance(point, polyline, targetDistance = null) {
  if (!polyline || polyline.length === 0) return Infinity;
  if (polyline.length === 1) {
    // Single point - just return distance to that point
    return Math.hypot(point[0] - polyline[0][0], point[1] - polyline[0][1]);
  }

  let minDistance = Infinity;
  const px = point[0];
  const py = point[1];

  for (let i = 0; i < polyline.length - 1; i++) {
    const x1 = polyline[i][0];
    const y1 = polyline[i][1];
    const x2 = polyline[i + 1][0];
    const y2 = polyline[i + 1][1];

    // Vector from segment start to end
    const dx = x2 - x1;
    const dy = y2 - y1;
    const segmentLengthSq = dx * dx + dy * dy;

    let distance;

    if (segmentLengthSq === 0) {
      // Segment is a point
      distance = Math.hypot(px - x1, py - y1);
    }
    else {
      // Project point onto the line segment, clamped to [0, 1]
      const t = Math.max(
        0,
        Math.min(1, ((px - x1) * dx + (py - y1) * dy) / segmentLengthSq),
      );

      // Find the closest point on the segment
      const closestX = x1 + t * dx;
      const closestY = y1 + t * dy;

      distance = Math.hypot(px - closestX, py - closestY);
    }

    if (distance < minDistance) {
      minDistance = distance;
      // Early exit if we're within target distance
      if (
        (targetDistance !== null && minDistance < targetDistance)
        || minDistance < 1
      ) {
        return minDistance;
      }
    }
  }

  return minDistance;
}

// mouse event handling states
function isOverContourLine(payload) {
  let result = null;
  if (!currentDrawingContours.value) return result;
  const currentPos = getCurrentCoordinates(payload);
  const threshold = Math.max(
    DISTANCE_CONTOUR_SELECTION_THRESHOLD,
    5 / (scaleFactor.value || 1),
  );
  let bestDistance = threshold;

  for (const [, ids] of Object.entries(currentDrawingContours.value)) {
    for (const [, contour] of Object.entries(ids)) {
      const contourLine = contour.getPolygonScaled();

      if (!contourLine.length) continue; // Skip empty contours
      if (contour.getProps().userEditable === false) {
        continue;
      }

      let scaledContour = convertPointArrayToArray(contourLine);
      if (scaledContour.length === 1) {
        scaledContour = scaledContour.concat(scaledContour);
      }
      const distance = pointToPolylineDistance(
        currentPos,
        scaledContour,
        bestDistance,
      );

      if (distance < bestDistance) {
        bestDistance = distance;
        result = contour.getProps();
      }
    }
  }
  return result;
}

function isOverSelectedContourCentroid(payload) {
  let result = null;
  if (!hasSelectedContour.value || !currentDrawingContours.value) return result;

  for (const [label, ids] of Object.entries(currentDrawingContours.value)) {
    for (const [id, contour] of Object.entries(ids)) {
      if (
        id !== selectedContour.value.id
        || label !== selectedContour.value.label
      ) {
        continue;
      }

      const centroid = contour.getCentroidScaled();
      const [currentPosX, currentPosY] = getCurrentCoordinates(payload);
      const distance = calculateDistancePoints(centroid, {
        x: currentPosX,
        y: currentPosY,
      });

      // Check if point is close enough to centroid
      if (
        distance * scaleFactor.value
        < DISTANCE_CENTROID_SELECTION_THRESHOLD
      ) {
        result = contour.getProps();
        return result; // Exit after finding first match
      }
    }
  }
  return result;
}

function isOverLineEndPoint(payload) {
  const result = { properties: null, staticPointIndex: 0 };
  if (!currentDrawingContours.value) return result;

  for (const [, ids] of Object.entries(currentDrawingContours.value)) {
    for (const [, contour] of Object.entries(ids)) {
      const contourType = contour.getProps().type;
      if (
        contourType !== ContourDefinitions.ContourType.LineContour
        && contourType !== ContourDefinitions.ContourType.AnnotationArrowContour
      ) {
        continue;
      }

      if (contour.getProps().userEditable === false) {
        continue;
      }

      const [currentPosX, currentPosY] = getCurrentCoordinates(payload);
      const contourLinePolygon = contour.getPolygonScaled();
      const scaledContour = convertPointArrayToArray(contourLinePolygon);

      const distanceFirstLinePoint = calculateDistance(scaledContour[0], [
        currentPosX,
        currentPosY,
      ]);
      const distanceSecondLinePoint = calculateDistance(scaledContour[1], [
        currentPosX,
        currentPosY,
      ]);

      if (
        distanceFirstLinePoint * scaleFactor.value
        < DISTANCE_LINE_ENDPOINT_SELECTION_THRESHOLD
        || distanceSecondLinePoint * scaleFactor.value
        < DISTANCE_LINE_ENDPOINT_SELECTION_THRESHOLD
      ) {
        if (distanceFirstLinePoint < distanceSecondLinePoint) {
          result.staticPointIndex = 1;
        }
        result.properties = contour.getProps();

        return result; // Exit after finding first match
      }
    }
  }
  return result;
}

function isOverPointContour(payload) {
  let result = null;
  if (!currentDrawingContours.value) return result;

  for (const [, ids] of Object.entries(currentDrawingContours.value)) {
    for (const [, contour] of Object.entries(ids)) {
      if (
        contour.getProps().type !== ContourDefinitions.ContourType.PointContour
      )
        continue;
      if (contour.getProps().userEditable === false) {
        continue;
      }

      const point = contour.getPolygonScaled()[0];
      const [currentPosX, currentPosY] = getCurrentCoordinates(payload);

      const distance = calculateDistancePoints(
        { x: currentPosX, y: currentPosY },
        point,
      );
      if (distance * scaleFactor.value < DISTANCE_POINT_SELECTION_THRESHOLD) {
        result = contour.getProps();
        return result;
      }
    }
  }
  return result;
}

function isOverTextContour(payload) {
  let result = null;
  if (!currentDrawingContours.value) return result;

  for (const [, ids] of Object.entries(currentDrawingContours.value)) {
    for (const [, contour] of Object.entries(ids)) {
      if (
        contour.getProps().type
        !== ContourDefinitions.ContourType.AnnotationTextContour
      ) {
        continue;
      }
      if (contour.getProps().userEditable === false) {
        continue;
      }

      const point = contour.getPolygonScaled()[0];
      const [currentPosX, currentPosY] = getCurrentCoordinates(payload);

      const distance = calculateDistancePoints(
        { x: currentPosX, y: currentPosY },
        point,
      );
      if (
        distance * scaleFactor.value
        < DISTANCE_POINT_SELECTION_THRESHOLD * 3
      ) {
        result = contour.getProps();
        return result;
      }
    }
  }
  return result;
}

function isClosedPolygonDrawing() {
  if (!currentDrawingContourProps.value) return false;
  const isClosedPolygon
    = currentDrawingContourProps.value.type
      === ContourDefinitions.ContourType.PolygonClosedContour;
  const modifyContour
    = hasSelectedContour.value
      && selectedContour.value.label === currentDrawingContourLabel.value;

  return isClosedPolygon && !modifyContour;
}

function getThresholdInnerContourProbs() {
  if (!THRESHOLD_SEGMENTATION_INNER_REGIONS) {
    return null;
  }
  if (
    currentDrawingContourProps.value.label
    === ContourDefinitions.ContourLabel.SaxLvEndo
  ) {
    return ContourDefinitions.ContourDefinitions.SaxLvPapillary;
  }
  if (
    currentDrawingContourProps.value.label
    === ContourDefinitions.ContourLabel.SaxRvEndo
  ) {
    return ContourDefinitions.ContourDefinitions.SaxRvPapillary;
  }
  return null;
}

function initNudgeTool() {
  if (
    hasSelectedContour.value
    && currentDrawingContourProps.value
    && (currentDrawingContourProps.value.type
      === ContourDefinitions.ContourType.PolygonClosedContour
      || currentDrawingContourProps.value.type
      === ContourDefinitions.ContourType.PolygonOpenContour)
  ) {
    const contourPolygon = contourContainerStore.getContour(curImageID.value, {
      label: selectedContour.value.label,
      id: selectedContour.value.id,
    });
    if (!contourPolygon || !contourPolygon.length) {
      nudgePolygon.value = [];
      nudgeRadius.value = 0;
      return;
    }

    nudgePolygon.value = contourPolygon;

    const [startPointX, startPointY] = screenToImage(
      mousePositionButtonDown.value[0],
      mousePositionButtonDown.value[1],
    );
    const startPoint = {
      x: startPointX,
      y: startPointY,
    };

    const closestPointIdx = getClosestPointIdx(startPoint, contourPolygon);
    const closestPoint = {
      x: contourPolygon[closestPointIdx].x,
      y: contourPolygon[closestPointIdx].y,
    };
    nudgeRadius.value = calculateDistancePoints(closestPoint, startPoint);
  }
  else {
    nudgePolygon.value = [];
    nudgeRadius.value = 0;
  }
}

function resetMouseStates() {
  mouseLeftButtonDown.value = false;
  mouseRightButtonDown.value = false;
  mouseMiddleButtonDown.value = false;

  mousePositionButtonDown.value = null;
  mousePathButtonDown.value = [];
  mousePathButtonDownThreshold.value = [];
  contourProbsButtonDown.value = null;
  lineEndPointProbsButtonDown.value = { properties: null, staticPointIndex: 0 };
  centroidContourProbsButtonDown.value = null;
  pointContourProbsButtonDown.value = null;
  movingPointProbs.value = null;
  movingLineEndpoints.value = null;

  mouseActionDrawContour.value = false;
  mouseActionMoveLineEndPoint.value = false;
  mouseActionMoveCentroid.value = false;
  mouseActionMovePoint.value = false;
  mouseActionSkipPointDraw.value = false;
  mouseActionThreshold.value = false;
  mouseActionNudgeTool.value = false;
  viewportStateStore.setMoveContour(viewID.value, null, '', [0, 0]);
  viewportStateStore.setHideDrawingContour(viewID.value, null, '');
  viewportStateStore.setHideDrawingContourLabel(viewID.value, null);
  thresholdSegmentation.value = null;
  nudgeRadius.value = 0;
  nudgePolygon.value = [];
}

function onMouseDown(payload) {
  if (!active.value) return;
  if (viewportStateStore.getToolMode(viewID.value) !== ViewportToolMode.Contour)
    return;

  updateLeftCornerCoords();

  if (
    viewportStateStore.getDrawingMode(viewID.value)
    === ViewportDrawingMode.PointClick
    && mouseActionDrawContour.value
  ) {
    mousePositionButtonDown.value = mouseEventToScreen(payload);
    mousePathButtonDown.value.push(mousePositionButtonDown.value);
    return;
  }

  resetMouseStates();
  mouseLeftButtonDown.value = payload.button === 0;
  mouseRightButtonDown.value = payload.button === 2;
  mouseLeftButtonDown.value = payload.button === 0 && !payload.ctrlKey;
  mouseRightButtonDown.value = isRightMouseButton(payload);
  mouseMiddleButtonDown.value = payload.button === 1;

  mousePositionButtonDown.value = mouseEventToScreen(payload);
  mousePathButtonDown.value.push(mousePositionButtonDown.value);
  contourProbsButtonDown.value = isOverContourLine(payload);
  lineEndPointProbsButtonDown.value = isOverLineEndPoint(payload);
  centroidContourProbsButtonDown.value = isOverSelectedContourCentroid(payload);
  pointContourProbsButtonDown.value = isOverPointContour(payload);
  const textContourProbsButtonDown = isOverTextContour(payload);

  if (mouseLeftButtonDown.value) {
    if (!currentDrawingContourProps.value) {
      resetMouseStates();
      return;
    }

    mouseActionDrawContour.value = true;

    // only one point to draw
    if (
      currentDrawingContourProps.value.type
      === ContourDefinitions.ContourType.PointContour
      && contourContainerStore.isPresentContourLabel(
        curImageID.value,
        currentDrawingContourLabel.value,
      )
    ) {
      mouseActionSkipPointDraw.value = true;
    }

    // check if on centroid
    if (
      centroidContourProbsButtonDown.value
      && centroidContourProbsButtonDown.value.type
      !== ContourDefinitions.ContourType.PointContour
      && centroidContourProbsButtonDown.value.type
      !== ContourDefinitions.ContourType.AnnotationTextContour
    ) {
      mouseActionDrawContour.value = false;
      mouseActionMoveCentroid.value = true;
    }
    // check if on line end point
    else if (lineEndPointProbsButtonDown.value.properties) {
      mouseActionDrawContour.value = false;
      mouseActionMoveLineEndPoint.value = true;

      viewportStateStore.setHideDrawingContour(
        viewID.value,
        lineEndPointProbsButtonDown.value.properties.label,
        lineEndPointProbsButtonDown.value.properties.id,
      );

      const line
        = currentDrawingContours.value?.[
          lineEndPointProbsButtonDown.value.properties.label
        ]?.[lineEndPointProbsButtonDown.value.properties.id];

      if (line) {
        const polygonScaled = line.getPolygonScaled();
        const staticIdx = lineEndPointProbsButtonDown.value.staticPointIndex;
        const movingIdx = 1 - staticIdx;
        const staticPoint = polygonScaled[staticIdx];
        const movingPoint = polygonScaled[movingIdx];
        if (staticPoint && movingPoint) {
          movingLineEndpoints.value = {
            staticPoint: [staticPoint.x, staticPoint.y],
            movingPoint: [movingPoint.x, movingPoint.y],
            staticPointIndex: staticIdx,
          };
        }
      }
      viewportStateStore.setDrawingContour(
        viewID.value,
        lineEndPointProbsButtonDown.value.properties.label,
      );
      viewportStateStore.setSelectedContour(
        viewID.value,
        lineEndPointProbsButtonDown.value.properties.label,
        lineEndPointProbsButtonDown.value.properties.id,
      );
      drawEndpointMovePreview();
    }
    // check if on point
    else if (pointContourProbsButtonDown.value) {
      mouseActionDrawContour.value = false;
      mouseActionMovePoint.value = true;
      movingPointProbs.value = {
        label: pointContourProbsButtonDown.value.label,
        id: pointContourProbsButtonDown.value.id,
      };
      viewportStateStore.setDrawingContour(
        viewID.value,
        pointContourProbsButtonDown.value.label,
      );
      viewportStateStore.setSelectedContour(
        viewID.value,
        pointContourProbsButtonDown.value.label,
        pointContourProbsButtonDown.value.id,
      );
    }
    else if (textContourProbsButtonDown) {
      mouseActionDrawContour.value = false;
      mouseActionMovePoint.value = true;
      movingPointProbs.value = {
        label: textContourProbsButtonDown.label,
        id: textContourProbsButtonDown.id,
      };
      viewportStateStore.setDrawingContour(
        viewID.value,
        textContourProbsButtonDown.label,
      );
      viewportStateStore.setSelectedContour(
        viewID.value,
        textContourProbsButtonDown.label,
        textContourProbsButtonDown.id,
      );
    }

    if (mouseActionDrawContour.value) {
      if (
        viewportStateStore.getDrawingMode(viewID.value)
        === ViewportDrawingMode.Threshold
      ) {
        if (
          currentDrawingContourProps.value.type
          !== ContourDefinitions.ContourType.PolygonClosedContour
        ) {
          resetMouseStates();
          return;
        }
        mouseActionThreshold.value = true;
        const image = currentStudyStore.getVtkImageData(curImageID.value);
        thresholdSegmentation.value = new ThresholdSegmentation(
          image,
          screenToImage(
            mousePositionButtonDown.value[0],
            mousePositionButtonDown.value[1],
          ),
        );

        const innerContourProbs = getThresholdInnerContourProbs();
        if (innerContourProbs) {
          viewportStateStore.setHideDrawingContourLabel(
            viewID.value,
            innerContourProbs.label,
          );
        }

        if (
          hasSelectedContour.value
          && selectedContour.value.label === currentDrawingContourLabel.value
        ) {
          viewportStateStore.setHideDrawingContour(
            viewID.value,
            selectedContour.value.label,
            selectedContour.value.id,
          );
        }
        return;
      }

      if (
        viewportStateStore.getDrawingMode(viewID.value)
        === ViewportDrawingMode.Nudge
      ) {
        if (
          contourProbsButtonDown.value
          && (!hasSelectedContour.value
            || !ContourDefinitions.equalContourIds(
              selectedContour.value,
              contourProbsButtonDown.value,
            ))
        ) {
          return;
        }
        if (!hasSelectedContour.value) {
          resetMouseStates();
          return;
        }
        if (selectedContour.value.label !== currentDrawingContourLabel.value) {
          viewportStateStore.setDrawingContour(
            viewID.value,
            selectedContour.value.label,
          );
        }
        initNudgeTool();
        if (!nudgePolygon.value.length) {
          resetMouseStates();
          return;
        }
        mouseActionNudgeTool.value = true;
        viewportStateStore.setHideDrawingContour(
          viewID.value,
          selectedContour.value.label,
          selectedContour.value.id,
        );
        drawNudgeTool();
      }
    }
  } // end left
}

function onMouseMove(payload) {
  if (!active.value) return;
  if (viewportStateStore.getToolMode(viewID.value) !== ViewportToolMode.Contour)
    return;
  if (!currentDrawingContourProps.value) return;

  if (mouseLeftButtonDown.value) {
    viewportStateStore.setDrawingActive(viewID.value, true);

    if (mouseActionDrawContour.value) {
      // freehand
      if (
        viewportStateStore.getDrawingMode(viewID.value)
        === ViewportDrawingMode.Freehand
      ) {
        mousePathButtonDown.value.push(mouseEventToScreen(payload));
      }
      // point click
      else if (
        viewportStateStore.getDrawingMode(viewID.value)
        === ViewportDrawingMode.PointClick
      ) {
        if (
          (currentDrawingContourProps.value.type
            === ContourDefinitions.ContourType.LineContour
            || currentDrawingContourProps.value.type
            === ContourDefinitions.ContourType.AnnotationArrowContour)
          && mousePathButtonDown.value.length === 1
        ) {
          mousePathButtonDown.value.push(mouseEventToScreen(payload));
        }
        else {
          mousePathButtonDown.value[mousePathButtonDown.value.length - 1]
            = mouseEventToScreen(payload);
        }
      }
      // threshold
      else if (
        viewportStateStore.getDrawingMode(viewID.value)
        === ViewportDrawingMode.Threshold
      ) {
        if (!mouseActionThreshold.value || !thresholdSegmentation.value) return;
        const eventCoords = mouseEventToScreen(payload);
        const diffX = eventCoords[0] - mousePositionButtonDown.value[0];
        const diffY = eventCoords[1] - mousePositionButtonDown.value[1];
        thresholdSegmentation.value.setThresholdsChange(
          diffX / canvasWidth.value,
          diffY / canvasHeight.value,
        );

        mousePathButtonDownThreshold.value
          = thresholdSegmentation.value.getContours();
        drawContourPathThreshold();
        return;
      }
      // nudge
      else if (
        viewportStateStore.getDrawingMode(viewID.value)
        === ViewportDrawingMode.Nudge
      ) {
        const point = mouseEventToScreen(payload);
        mousePathButtonDown.value.push(point);
        if (!mouseActionNudgeTool.value || !nudgePolygon.value.length) return;

        const [centerX, centerY] = screenToImage(point[0], point[1]);
        nudgePolygon.value = convertArrayToPointArray(
          nudgePoints2D(convertPointArrayToArray(nudgePolygon.value), {
            center: [centerX, centerY],
            radius: nudgeRadius.value,
          }),
        );
      }
      // none
      else if (
        viewportStateStore.getDrawingMode(viewID.value)
        === ViewportDrawingMode.None
      ) {
        mousePathButtonDown.value.push(mouseEventToScreen(payload));
      }

      // draw
      if (
        currentDrawingContourProps.value.type
        === ContourDefinitions.ContourType.LineContour
      ) {
        drawLine();
      }
      else if (
        currentDrawingContourProps.value.type
        === ContourDefinitions.ContourType.AnnotationArrowContour
      ) {
        drawArrow();
      }
      else if (
        currentDrawingContourProps.value.type
        === ContourDefinitions.ContourType.PointContour
        || currentDrawingContourProps.value.type
        === ContourDefinitions.ContourType.AnnotationTextContour
      ) {
        // drawPoint();
      }
      else if (
        viewportStateStore.getDrawingMode(viewID.value)
        === ViewportDrawingMode.Nudge
      ) {
        if (mouseActionNudgeTool.value) {
          drawNudgeTool();
        }
      }
      else {
        drawContourPath();
      }
    }

    if (mouseActionMoveLineEndPoint.value) {
      mousePathButtonDown.value.push(mouseEventToScreen(payload));
      drawEndpointMovePreview();
    }

    if (mouseActionMoveCentroid.value) {
      mousePathButtonDown.value.push(mouseEventToScreen(payload));
      const startPoint = mousePathButtonDown.value[0];
      const endPoint
        = mousePathButtonDown.value[mousePathButtonDown.value.length - 1];
      const s = screenToImage(startPoint[0], startPoint[1]);
      const e = screenToImage(endPoint[0], endPoint[1]);
      const distancePoint = [e[0] - s[0], e[1] - s[1]];
      viewportStateStore.setMoveContour(
        viewID.value,
        centroidContourProbsButtonDown.value.label,
        centroidContourProbsButtonDown.value.id,
        distancePoint,
      );
    }

    if (mouseActionMovePoint.value && movingPointProbs.value) {
      mousePathButtonDown.value.push(mouseEventToScreen(payload));
      const startPoint = mousePathButtonDown.value[0];
      const endPoint
        = mousePathButtonDown.value[mousePathButtonDown.value.length - 1];
      const s = screenToImage(startPoint[0], startPoint[1]);
      const e = screenToImage(endPoint[0], endPoint[1]);
      const distancePoint = [e[0] - s[0], e[1] - s[1]];
      viewportStateStore.setMoveContour(
        viewID.value,
        movingPointProbs.value.label,
        movingPointProbs.value.id,
        distancePoint,
      );
    }
  } // end left
}

function stopDrawing() {
  resetMouseStates();
  viewportStateStore.setDrawingActive(viewID.value, false);

  // Defer removing the live SVG until the VTK actor has painted
  // (createImageActors runs on a watcher tick, then renderLater paints on rAF).
  // Removing only the captured node avoids wiping a freshly started drawing.
  const staleSvgNode = svg.value?.node?.();
  svg.value = null;
  if (staleSvgNode) {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => staleSvgNode.remove()),
    );
  }
  else {
    resetDrawingCanvas();
  }
}

function getMovedLineEndpointsImage() {
  if (!movingLineEndpoints.value || !mousePathButtonDown.value.length) {
    return null;
  }
  const { staticPoint, movingPoint, staticPointIndex } = movingLineEndpoints.value;
  const startScreen = mousePathButtonDown.value[0];
  const endScreen = mousePathButtonDown.value[mousePathButtonDown.value.length - 1];
  const s = screenToImage(startScreen[0], startScreen[1]);
  const e = screenToImage(endScreen[0], endScreen[1]);
  const dx = e[0] - s[0];
  const dy = e[1] - s[1];
  const pts = [];
  pts[staticPointIndex] = [staticPoint[0], staticPoint[1]];
  pts[1 - staticPointIndex] = [movingPoint[0] + dx, movingPoint[1] + dy];
  return pts;
}

function onMouseUp(payload) {
  if (
    !mouseLeftButtonDown.value
    && !mouseRightButtonDown.value
    && !mouseMiddleButtonDown.value
  ) {
    return;
  }
  if (viewportStateStore.getToolMode(viewID.value) !== ViewportToolMode.Contour) {
    stopDrawing();
    return;
  }

  mousePathButtonDown.value.push(mouseEventToScreen(payload));
  const travelDistance = calculateDistance(
    mousePathButtonDown.value[0],
    mousePathButtonDown.value[mousePathButtonDown.value.length - 1],
  );

  if (mouseLeftButtonDown.value) {
    if (mouseActionDrawContour.value) {
      // detect click
      if (travelDistance < MIN_SELECTION_CLICK_TRAVEL_DISTANCE) {
        if (contourProbsButtonDown.value) {
          if (
            viewportStateStore.getDrawingMode(viewID.value)
            !== ViewportDrawingMode.PointClick
          ) {
            viewportStateStore.setSelectedContour(
              viewID.value,
              contourProbsButtonDown.value.label,
              contourProbsButtonDown.value.id,
            );
            viewportStateStore.setDrawingContour(
              viewID.value,
              contourProbsButtonDown.value.label,
            );
            stopDrawing();
            return;
          }
        }
        else if (
          viewportStateStore.getDrawingMode(viewID.value)
          !== ViewportDrawingMode.PointClick
        ) {
          if (hasSelectedContour.value) {
            viewportStateStore.setSelectedContour(viewID.value, null, null);
            stopDrawing();
            return;
          }
          else if (
            currentDrawingContourProps.value?.type
            !== ContourDefinitions.ContourType.PointContour
            && currentDrawingContourProps.value?.type
            !== ContourDefinitions.ContourType.AnnotationTextContour
          ) {
            viewportStateStore.setDrawingContour(viewID.value, null);
            viewportStateStore.setToolMode(
              viewID.value,
              ViewportToolMode.Pan,
            );
            stopDrawing();
            return;
          }
        }
      }

      if (!mouseActionSkipPointDraw.value) {
        // point click
        if (
          viewportStateStore.getDrawingMode(viewID.value)
          === ViewportDrawingMode.PointClick
          && (currentDrawingContourProps.value.type
            === ContourDefinitions.ContourType.PolygonClosedContour
            || currentDrawingContourProps.value.type
            === ContourDefinitions.ContourType.PolygonOpenContour)
        ) {
          if (mousePathButtonDown.value.length > 2) {
            // Get all elements except the second-to-last one
            const newArray = [
              ...mousePathButtonDown.value.slice(
                0,
                mousePathButtonDown.value.length - 2,
              ),
              mousePathButtonDown.value[mousePathButtonDown.value.length - 1],
            ];
            mousePathButtonDown.value = newArray;
          }
          return;
        }

        // threshold
        if (
          viewportStateStore.getDrawingMode(viewID.value)
          === ViewportDrawingMode.Threshold
        ) {
          if (travelDistance > MIN_SELECTION_CLICK_TRAVEL_DISTANCE) {
            const innerContourProbs = getThresholdInnerContourProbs();
            // remove all inner contours
            if (innerContourProbs) {
              contourContainerStore.deleteAllContoursOfLabel(
                curImageID.value,
                innerContourProbs.label,
                false,
              );
            }

            for (const [
              idx,
              contour,
            ] of mousePathButtonDownThreshold.value.entries()) {
              if (contour.length < 3) {
                // if outer contour not valid break
                if (idx === 0) {
                  stopDrawing();
                  return;
                }

                continue;
              }

              let contourLabel = currentDrawingContourLabel.value;
              if (idx > 0 && !innerContourProbs) {
                stopDrawing();
                return;
              }

              if (idx > 0) {
                contourLabel = innerContourProbs.label;
              }

              let id = encodeContourLabelItemId([-1]);
              if (
                hasSelectedContour.value
                && selectedContour.value.label === contourLabel
              ) {
                id = selectedContour.value.id;
              }
              contourContainerStore.addContour(
                curImageID.value,
                convertArrayToPointArray(contour),
                contourLabel,
                id,
                true, // triggerEvent
                true,
                true,
              );
            }
          }
          stopDrawing();
          return;
        }

        // nudge tool
        if (
          viewportStateStore.getDrawingMode(viewID.value)
          === ViewportDrawingMode.Nudge
        ) {
          if (
            mouseActionNudgeTool.value
            && hasSelectedContour.value
            && nudgePolygon.value.length >= 3
          ) {
            viewportStateStore.setHideDrawingContour(viewID.value, null, '');
            contourContainerStore.addContour(
              curImageID.value,
              nudgePolygon.value,
              selectedContour.value.label,
              selectedContour.value.id,
              true, // triggerEvent
              true,
              true,
            );
          }

          stopDrawing();
          return;
        }

        // freehand
        let contourLine = mousePathButtonDown.value;
        const contourType = currentDrawingContourProps.value?.type;
        if (
          contourType === ContourDefinitions.ContourType.LineContour
          || contourType === ContourDefinitions.ContourType.AnnotationArrowContour
        ) {
          if (travelDistance < MIN_SELECTION_CLICK_TRAVEL_DISTANCE) {
            stopDrawing();
            return;
          }
          contourLine = [contourLine[0], contourLine[contourLine.length - 1]];
        }
        else if (
          contourType === ContourDefinitions.ContourType.PointContour
          || contourType === ContourDefinitions.ContourType.AnnotationTextContour
        ) {
          contourLine = [contourLine[contourLine.length - 1]];
        }
        else if (
          contourLine.length < 3
          || travelDistance < MIN_SELECTION_CLICK_TRAVEL_DISTANCE
        ) {
          stopDrawing();
          return;
        }

        // polygon
        contourLine = contourLine.map(point =>
          screenToImage(point[0], point[1]),
        );

        let id = encodeContourLabelItemId([-1]);
        if (
          hasSelectedContour.value
          && selectedContour.value.label === currentDrawingContourLabel.value
        ) {
          id = selectedContour.value.id;
        }
        if (
          contourType === ContourDefinitions.ContourType.AnnotationTextContour
        ) {
          showAnnotationInput({
            event: payload,
            contourLine,
            label: currentDrawingContourLabel.value,
            id,
          });
          stopDrawing();
          return;
        }

        contourContainerStore.addContour(
          curImageID.value,
          convertArrayToPointArray(contourLine),
          currentDrawingContourLabel.value,
          id,
        );
      }
    }

    if (mouseActionMoveLineEndPoint.value) {
      const contourLine = getMovedLineEndpointsImage();
      viewportStateStore.setHideDrawingContour(viewID.value, null, '');
      if (contourLine) {
        contourContainerStore.addContour(
          curImageID.value,
          convertArrayToPointArray(contourLine),
          lineEndPointProbsButtonDown.value.properties.label,
          lineEndPointProbsButtonDown.value.properties.id,
        );
      }
    }

    if (
      mouseActionMoveCentroid.value
      && centroidContourProbsButtonDown.value
      && centroidContourProbsButtonDown.value.type
      !== ContourDefinitions.ContourType.PointContour
    ) {
      const startPoint = mousePathButtonDown.value[0];
      const endPoint
        = mousePathButtonDown.value[mousePathButtonDown.value.length - 1];
      const s = screenToImage(startPoint[0], startPoint[1]);
      const e = screenToImage(endPoint[0], endPoint[1]);
      const distancePoint = [e[0] - s[0], e[1] - s[1]];

      viewportStateStore.setMoveContour(viewID.value, null, '', [0, 0]);
      if (travelDistance > 0) {
        contourContainerStore.translateContour(
          curImageID.value,
          {
            label: centroidContourProbsButtonDown.value.label,
            id: centroidContourProbsButtonDown.value.id,
          },
          distancePoint,
        );
      }
    }

    if (mouseActionMovePoint.value && movingPointProbs.value) {
      if (travelDistance < MIN_SELECTION_CLICK_TRAVEL_DISTANCE) {
        if (
          movingPointProbs.value.label
          !== ContourDefinitions.ContourLabel.LaxLvEndoBasalPoint
        ) {
          viewportStateStore.setSelectedContour(
            viewID.value,
            movingPointProbs.value.label,
            movingPointProbs.value.id,
          );
          viewportStateStore.setDrawingContour(
            viewID.value,
            movingPointProbs.value.label,
          );
        }
        viewportStateStore.setMoveContour(viewID.value, null, '', [0, 0]);
      }
      else {
        const startPoint = mousePathButtonDown.value[0];
        const endPoint
          = mousePathButtonDown.value[mousePathButtonDown.value.length - 1];
        const s = screenToImage(startPoint[0], startPoint[1]);
        const e = screenToImage(endPoint[0], endPoint[1]);
        const distancePoint = [e[0] - s[0], e[1] - s[1]];

        viewportStateStore.setMoveContour(viewID.value, null, '', [0, 0]);
        contourContainerStore.translateContour(
          curImageID.value,
          {
            label: movingPointProbs.value.label,
            id: movingPointProbs.value.id,
          },
          distancePoint,
        );
      }
    }
  }
  stopDrawing();
}

function onMouseClick(payload) {
  if (!active.value || !showContours.value) return;
  // check for contour selection in other modes than contour drawing
  if (viewportStateStore.getToolMode(viewID.value) === ViewportToolMode.Contour)
    return;

  updateLeftCornerCoords();
  resetMouseStates();

  contourProbsButtonDown.value = isOverContourLine(payload);
  lineEndPointProbsButtonDown.value = isOverLineEndPoint(payload);
  centroidContourProbsButtonDown.value = isOverSelectedContourCentroid(payload);
  pointContourProbsButtonDown.value = isOverPointContour(payload);
  const textContourProbsButtonDown = isOverTextContour(payload);

  let selection = null;

  if (contourProbsButtonDown.value) {
    selection = contourProbsButtonDown.value;
  }

  // only line needs check for probs
  if (lineEndPointProbsButtonDown.value.properties) {
    selection = lineEndPointProbsButtonDown.value.properties;
  }

  if (centroidContourProbsButtonDown.value) {
    selection = centroidContourProbsButtonDown.value;
  }

  if (pointContourProbsButtonDown.value) {
    selection = pointContourProbsButtonDown.value;
  }
  if (textContourProbsButtonDown) {
    selection = textContourProbsButtonDown;
  }

  if (selection) {
    viewportStateStore.setSelectedContour(
      viewID.value,
      selection.label,
      selection.id,
    );
    viewportStateStore.setDrawingContour(viewID.value, selection.label);
    viewportStateStore.setToolMode(viewID.value, ViewportToolMode.Contour);
  }

  resetMouseStates();
}

function onMouseDoubleClick(payload) {
  if (!active.value) return;
  // if (viewportStateStore.getToolMode(viewID.value) !== ViewportToolMode.Contour) return;

  if (!currentDrawingContourProps.value) {
    return;
  }
  // if (viewportStateStore.getToolMode(viewID.value) !== ViewportToolMode.Contour) return;
  if (
    viewportStateStore.getDrawingMode(viewID.value)
    !== ViewportDrawingMode.PointClick
  ) {
    return;
  }

  mousePathButtonDown.value.push(mouseEventToScreen(payload));

  // remove duplicate end points
  if (mousePathButtonDown.value.length > 5) {
    // Remove duplicate points at the end of array
    const lastPoint
      = mousePathButtonDown.value[mousePathButtonDown.value.length - 1];
    while (
      mousePathButtonDown.value.length
      && equalPoints(
        mousePathButtonDown.value[mousePathButtonDown.value.length - 1],
        lastPoint,
      )
    ) {
      mousePathButtonDown.value.pop();
    }
    mousePathButtonDown.value.push(lastPoint);

    if (mousePathButtonDown.value.length < 3) {
      // abort drawing
      return;
    }

    const interpolator = new CurveInterpolator(mousePathButtonDown.value, {
      tension: 0,
      alpha: 0.7,
      closed: isClosedPolygonDrawing(),
    });
    const segments = 80;
    let pts = interpolator.getPoints(segments);

    pts = pts.map(point => screenToImage(point[0], point[1]));

    let id = encodeContourLabelItemId([-1]);
    if (
      hasSelectedContour.value
      && selectedContour.value.label === currentDrawingContourLabel.value
    ) {
      id = selectedContour.value.id;
    }

    contourContainerStore.addContour(
      curImageID.value,
      convertArrayToPointArray(pts),
      currentDrawingContourLabel.value,
      id,
    );
  }
  resetMouseStates();
  resetDrawingCanvas();
}

function stopPropagation(event) {
  if (event && event.stopPropagation) {
    event.stopPropagation();
  }
  if (event && event.preventDefault) {
    event.preventDefault();
  }
}

const mouseMoving = ref(false);
const mouseDown = ref(false);

onMounted(() => {
  showContextMenu.value = false;
  setupRightDragListeners();

  onEvent('mousedbclick', (payload) => {
    if (!active.value || !showContours.value) return;
    onMouseDoubleClick(payload);
    stopPropagation(payload);
  });
  onEvent('mouseclick', (payload) => {
    if (!active.value || !showContours.value) return;
    mouseMoving.value = false; // Reset mouse moving state

    if (performance.now() - contextMenuOpenedAt < 100) {
      return;
    }

    closeContextMenu();
    onMouseClick(payload);
  });
  onEvent('contextmenu', (payload) => {
    if (!showContours.value) return;
    if (!overlayContainer.value) return;

    const rect = overlayContainer.value.getBoundingClientRect();
    const isInside = (
      payload.clientX >= rect.left
      && payload.clientX <= rect.right
      && payload.clientY >= rect.top
      && payload.clientY <= rect.bottom
    );

    if (!isInside) {
      if (showContextMenu.value) {
        closeContextMenu();
      }
      return;
    }

    let wasDragged = rightMouseDragged;
    if (rightMouseDownPos) {
      const dist = Math.hypot(
        payload.clientX - rightMouseDownPos.x,
        payload.clientY - rightMouseDownPos.y,
      );
      if (dist > RIGHT_CLICK_DRAG_THRESHOLD) {
        wasDragged = true;
      }
    }

    // On macOS, `contextmenu` fires immediately on mousedown while the right
    // button is still pressed down (before any drag-to-zoom can occur).
    // Defer opening the pie menu until the button is released without dragging.
    if (rightMouseDown || (payload.buttons !== undefined && payload.buttons !== 0)) {
      if (!rightMouseDown) {
        rightMouseDown = true;
        rightMouseDownPos = { x: payload.clientX, y: payload.clientY };
        rightMouseDragged = false;
      }
      if (!wasDragged) {
        pendingContextMenuEvent = {
          clientX: payload.clientX,
          clientY: payload.clientY,
        };
      }
      return;
    }

    if (rightDragTimeout) {
      clearTimeout(rightDragTimeout);
      rightDragTimeout = null;
    }
    rightMouseDownPos = null;
    rightMouseDragged = false;
    pendingContextMenuEvent = null;
    mouseMoving.value = false;

    if (wasDragged) {
      return;
    }

    initContextMenu(payload);
  });
  onEvent('mousedown', (payload) => {
    if (!active.value || !showContours.value) return;
    mouseDown.value = true;
    mouseMoving.value = false; // Reset mouse moving state

    if (isRightMouseButton(payload) && !rightMouseDown) {
      if (rightDragTimeout) {
        clearTimeout(rightDragTimeout);
        rightDragTimeout = null;
      }
      rightMouseDown = true;
      rightMouseDownPos = { x: payload.clientX, y: payload.clientY };
      rightMouseDragged = false;
      pendingContextMenuEvent = null;
    }

    onMouseDown(payload);
  });

  onEvent('mousemove', (payload) => {
    if (!showContours.value) return;
    if (!mouseDown.value) return;
    mouseMoving.value = true;

    if (rightMouseDownPos) {
      const dist = Math.hypot(
        payload.clientX - rightMouseDownPos.x,
        payload.clientY - rightMouseDownPos.y,
      );
      if (dist > RIGHT_CLICK_DRAG_THRESHOLD) {
        rightMouseDragged = true;
        pendingContextMenuEvent = null;
      }
    }

    onMouseMove(payload);
  });

  onEvent('mouseup', (payload) => {
    if (!showContours.value) return;
    if (!mouseDown.value) return;
    mouseDown.value = false;

    if (rightMouseDownPos) {
      const dist = Math.hypot(
        payload.clientX - rightMouseDownPos.x,
        payload.clientY - rightMouseDownPos.y,
      );
      if (dist > RIGHT_CLICK_DRAG_THRESHOLD) {
        rightMouseDragged = true;
      }
    }

    onWindowPointerUp(payload);
    onMouseUp(payload);
  });

  onEvent('mouseenter', () => {
    //
  });

  onEvent('mouseleave', () => {
    // resetMouseStates();
  });

  updateLeftCornerCoords();
});

onBeforeUnmount(() => {
  if (activePieMenuViewId.value === viewID.value) {
    activePieMenuViewId.value = null;
  }
  if (typeof window !== 'undefined') {
    window.removeEventListener('pointerdown', onGlobalPointerDown, { capture: true });
  }
  cleanupRightDragListeners();
});

function resetDrawingCanvas() {
  d3.select(overlay.value).selectAll('svg').remove();
  svg.value = d3
    .select(overlay.value)
    .append('svg')
    .attr('width', '100%')
    .attr('height', '100%');
}

function drawNudgeTool() {
  const group = createDrawGroup();
  const line = getLineGenerator();
  const strokeColor = currentDrawingContourProps.value.color;
  const center = screenToImage(
    mousePathButtonDown.value[mousePathButtonDown.value.length - 1][0],
    mousePathButtonDown.value[mousePathButtonDown.value.length - 1][1],
  );
  const nudgePolygonPts = nudgePolygon.value.map(point => [point.x, point.y]);

  // Create a unique ID for this gradient
  const gradientId = `nudgeToolGradient-${Date.now()}`;
  // Gradient lives in <defs> (referenced by id, not positioned).
  const gradient = svg.value
    .append('defs')
    .append('radialGradient')
    .attr('id', gradientId)
    .attr('cx', '50%')
    .attr('cy', '50%')
    .attr('r', '50%')
    .attr('fx', '50%')
    .attr('fy', '50%');

  // Add color stops to the gradient
  gradient
    .append('stop')
    .attr('offset', '0%')
    .attr('stop-color', strokeColor)
    .attr('stop-opacity', 0.5);

  gradient
    .append('stop')
    .attr('offset', '70%')
    .attr('stop-color', strokeColor)
    .attr('stop-opacity', 0.4);

  gradient
    .append('stop')
    .attr('offset', '100%')
    .attr('stop-color', strokeColor)
    .attr('stop-opacity', 0.3);

  // The nudge radius is in image px and scales with the image, so it is drawn
  // straight under the group matrix (no counter-scaling). Only the outline is
  // kept at a constant on-screen width via non-scaling-stroke.
  group
    .append('circle')
    .attr('cx', center[0])
    .attr('cy', center[1])
    .attr('r', nudgeRadius.value)
    .attr('fill', `url(#${gradientId})`)
    .attr('stroke', 'white')
    .attr('stroke-width', 1)
    .attr('vector-effect', 'non-scaling-stroke');

  group
    .append('path')
    .datum(nudgePolygonPts)
    .attr('fill', 'none')
    .attr('opacity', 1)
    .attr('stroke-linejoin', 'round') // Set line join style to round
    .attr('stroke-linecap', 'round')
    .attr('stroke', strokeColor)
    .attr('stroke-width', CONTOUR_STROKE)
    .attr('vector-effect', 'non-scaling-stroke')
    .attr('d', line);
}

function drawContourPath(resetCanvas = true) {
  const group = resetCanvas
    ? createDrawGroup()
    : svg.value.select('g.draw-root');

  const line = getLineGenerator();
  const strokeColor = currentDrawingContourProps.value.color;

  // Interpolation / hit-testing stays in screen space (unchanged behaviour);
  // points are converted to image space only when appended to the group matrix.
  const toImage = p => screenToImage(p[0], p[1]);
  const strokePath = (screenPts, color, opacity) => {
    group
      .append('path')
      .datum(screenPts.map(toImage))
      .attr('fill', 'none')
      .attr('opacity', opacity)
      .attr('stroke-linejoin', 'round') // Set line join style to round
      .attr('stroke-linecap', 'round')
      .attr('stroke', color)
      .attr('stroke-width', CONTOUR_STROKE)
      .attr('vector-effect', 'non-scaling-stroke')
      .attr('d', line);
  };

  let drawPath = mousePathButtonDown.value;

  if (
    viewportStateStore.getDrawingMode(viewID.value)
    === ViewportDrawingMode.PointClick
  ) {
    if (mousePathButtonDown.value.length < 2) {
      return;
    }
    const isClosedDrawing = isClosedPolygonDrawing();
    const interpolator = new CurveInterpolator(mousePathButtonDown.value, {
      tension: 0,
      alpha: 0.7,
      closed: isClosedDrawing,
    });
    const segments = 80;
    const pts = interpolator.getPoints(segments);
    drawPath = pts;

    if (isClosedDrawing) {
      const closestPointIdxLast = getClosestPointArrayIdx(
        mousePathButtonDown.value[mousePathButtonDown.value.length - 1],
        drawPath,
      );

      // Split drawPath at the closest point
      const firstPathSegment = drawPath.slice(0, closestPointIdxLast + 1);
      const secondPathSegment = drawPath.slice(closestPointIdxLast);

      strokePath(firstPathSegment, strokeColor, 1);
      strokePath(secondPathSegment, 'white', 0.15);

      return;
    }
  }

  strokePath(drawPath, strokeColor, 1);
}

function drawContourPathThreshold() {
  const group = createDrawGroup();
  const line = getLineGenerator();

  if (!mousePathButtonDownThreshold.value.length) {
    return;
  }
  let strokeColor = currentDrawingContourProps.value.color;
  const innerContourProbs = getThresholdInnerContourProbs();

  // Threshold contours are already in image-pixel space, so they are drawn
  // straight into the group matrix (no imageToScreen conversion needed).
  const strokePath = (pts, color) => {
    group
      .append('path')
      .datum(pts)
      .attr('fill', 'none')
      .attr('opacity', 1)
      .attr('stroke-linejoin', 'round') // Set line join style to round
      .attr('stroke-linecap', 'round')
      .attr('stroke', color)
      .attr('stroke-width', CONTOUR_STROKE)
      .attr('vector-effect', 'non-scaling-stroke')
      .attr('d', line);
  };

  // first is outer, rest are inner

  for (const [idx, contour] of mousePathButtonDownThreshold.value.entries()) {
    if (contour.length < 3) {
      // if outer contour not valid break
      if (idx === 0) {
        return;
      }

      continue;
    }

    if (idx > 0 && !innerContourProbs) {
      return;
    }

    if (idx > 0) {
      strokeColor = innerContourProbs.color;
    }

    strokePath(contour, strokeColor);
  }
}

function drawEndpointMovePreview() {
  const ptsImage = getMovedLineEndpointsImage();
  if (!ptsImage) return;
  const startScreen = imageToScreen(ptsImage[0][0], ptsImage[0][1]);
  const endScreen = imageToScreen(ptsImage[1][0], ptsImage[1][1]);
  if (
    currentDrawingContourProps.value?.type
    === ContourDefinitions.ContourType.AnnotationArrowContour
  ) {
    drawArrow(startScreen, endScreen);
  }
  else {
    drawLine(startScreen, endScreen);
  }
}

// Live-drawing group in image-pixel space. It carries the image->screen matrix,
// so draws made with the shared primitives match the committed contours drawn by
// UIDDisplayContours (same sizes, rotation and non-scaling strokes).
function createDrawGroup() {
  resetDrawingCanvas();
  return svg.value
    .append('g')
    .attr('class', 'draw-root')
    .attr('transform', overlayTransform.value);
}

// Counter-scales circle radii / font sizes and keeps text upright, mirroring the
// display overlay. Call once after all primitives are appended to the group.
function finalizeDrawGroup(group) {
  applyGroupTransform(group, {
    overlayTransform: overlayTransform.value,
    scaleFactor: scaleFactor.value,
    imageToScreenMatrix: imageToScreenMatrix.value,
  });
}

function drawArrow(
  startPoint = mousePathButtonDown.value[0],
  endPoint = mousePathButtonDown.value[mousePathButtonDown.value.length - 1],
) {
  const strokeColor = currentDrawingContourProps.value.color;
  const group = createDrawGroup();

  const start = screenToImage(startPoint[0], startPoint[1]);
  const end = screenToImage(endPoint[0], endPoint[1]);

  // Same shape helper as the committed arrow in UIDDisplayContours.
  appendArrow(group, start, end, strokeColor, 1);
}

function drawLine(
  startPoint = mousePathButtonDown.value[0],
  endPoint = mousePathButtonDown.value[mousePathButtonDown.value.length - 1],
) {
  const strokeColor = currentDrawingContourProps.value.color;
  const group = createDrawGroup();

  const start = screenToImage(startPoint[0], startPoint[1]);
  const end = screenToImage(endPoint[0], endPoint[1]);
  const line = [start, end];

  appendPath(group, line, strokeColor, 1, CONTOUR_STROKE);

  appendCircle(group, start[0], start[1], ENDPOINT_RADIUS, { fill: strokeColor });
  appendCircle(group, end[0], end[1], ENDPOINT_RADIUS, { fill: strokeColor });

  // Drawn under the group matrix, so the crossbar offset stays in image px
  // (no `linear` argument) — same as the committed line in UIDDisplayContours.
  if (
    currentDrawingContourProps.value?.label
    === ContourDefinitions.ContourLabel.LaxLvRange
  ) {
    drawRangeCrossbars(group, line, localizerDir2D.value, {
      color: strokeColor,
      opacity: RANGE_CROSS_OPACITY,
      strokeWidth: RANGE_CROSS_STROKE,
      nonScalingStroke: true,
    });
  }

  finalizeDrawGroup(group);
}

// Re-applies the image->screen matrix to a live drawing if the zoom changes
// while it is on screen. Draws now live under a group matrix with
// non-scaling-strokes, so this just refreshes the group transform + handle
// sizes (mirrors UIDDisplayContours).
function applyTransform() {
  const group = d3.select(overlay.value).select('g.draw-root');
  applyGroupTransform(group, {
    overlayTransform: overlayTransform.value,
    scaleFactor: scaleFactor.value,
    imageToScreenMatrix: imageToScreenMatrix.value,
  });
}

watch(
  () => overlayTransform.value,
  () => {
    requestAnimationFrame(applyTransform);
  },
  { immediate: true },
);

watch(
  () => curImageID.value,
  (newUid) => {
    stopDrawing();
    if (
      hasSelectedContour.value
      && newUid
      && !contourContainerStore.isPresentContourContourId(
        newUid,
        selectedContour.value,
      )
    ) {
      viewportStateStore.setSelectedContour(viewID.value, null, null);
    }
  },
);

watch(
  () => showContours.value,
  (val) => {
    if (!val) {
      closeContextMenu();
      stopDrawing();
      cancelAnnotationInput();
      resetMouseStates();
      if (viewportStateStore.getToolMode(viewID.value) === ViewportToolMode.Contour) {
        viewportStateStore.setToolMode(viewID.value, ViewportToolMode.Pan);
      }
      viewportStateStore.setSelectedContour(viewID.value, null, null);
      viewportStateStore.setDrawingContour(viewID.value, null);
    }
  },
);
</script>

<template>
  <div ref="overlayContainer" class="overlay">
    <div
      ref="overlay"
      style="will-change: transform"
      :style="{
        position: 'absolute',
        left: '0px',
        top: '0px',
        width: '100%',
        height: '100%',
      }"
    />
  </div>
  <div
    v-show="annotationInputVisible"
    class="pie-menu-container-center"
    @mousedown.stop.prevent="forceAnnotationCommit"
  >
    <TextAnnotationInput
      ref="annotationInputRef"
      :visible="annotationInputVisible"
      :annotation-input-position="annotationInputPosition"
      :existing-text="annotationInputValue"
      @commit="commitAnnotationInput"
      @cancel="cancelAnnotationInput"
    />
  </div>
  <div
    v-if="showContextMenu"
    ref="pieMenuContainerRef"
    class="pie-menu-container-center"
    @pointerdown.self="closeContextMenu"
    @contextmenu.self.prevent="closeContextMenu"
  >
    <PieMenu
      :view-id="viewID"
      :current-image-u-i-d="curImageID"
      :click-x="contextMenuCoords[0]"
      :click-y="contextMenuCoords[1]"
      @close="closeContextMenu"
    />
  </div>
</template>

<style scoped>
.overlay {
  position: absolute;
  pointer-events: none;
  z-index: 0;
  width: 100%;
  height: 100%;
}

.pie-menu-container-center {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  z-index: 1000;
  pointer-events: all;
}

.pie-menu-container-center :deep(.pie-menu) {
  position: absolute;
  transform: translate(-50%, -50%);
  pointer-events: all;
}
</style>
